import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { apiService } from '../../services/apiService';
import { PartDemand } from '../../services/partDemandTypes';
import { ForecastTable } from '../partDemand/ForecastSection';
import { LoadingSpinner } from '../LoadingSpinner';
import { ErrorBanner } from '../tools/ErrorBanner';

/** The Part Demand & Forecast dashboard's forecast, in short, with a link to the full dashboard */
export const PartForecastCard: React.FC<{ partNum: string }> = ({
  partNum,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<PartDemand | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    setError(null);
    apiService.getPartDemand(partNum).then(
      (demand) => active && setData(demand),
      (e: Error) => active && setError(e.message)
    );
    return () => {
      active = false;
    };
  }, [partNum]);

  if (error) {
    return <ErrorBanner error={error} onDismiss={() => setError(null)} />;
  }

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      <div className="bg-amber-50 px-4 py-3 border-b border-amber-200 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-lg font-bold text-amber-900">
            {t('parts.forecast.title')}
          </h3>
          <p className="text-xs text-amber-700">
            {t('partDemand.forecast.subtitle')}
          </p>
        </div>
        <Link
          to={`/part-demand/${encodeURIComponent(partNum)}`}
          className="text-sm font-medium text-blue-700 hover:underline"
        >
          {t('parts.forecast.openDashboard')}
        </Link>
      </div>
      {data ? (
        <ForecastTable data={data} />
      ) : (
        <div className="p-4 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};
